import { useEffect, useRef, useSyncExternalStore } from 'react';
import { startPipAnchorBridge } from '@/features/picture-in-picture/anchor-bridge';
import type { PipItem } from '@/features/picture-in-picture/pip-media';
import { followPipStills, keepPipStills } from '@/features/picture-in-picture/pip-stills';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { electronInvoke, isElectronDesktop } from '@/lib/electron-bridge';

/** Where a conversation's picture-in-picture is: the summary panel, or the floating window. */
export type PipPlacement = 'home' | 'pinned';

/** The conversations' picture-in-picture, as the Desktop keeps it. */
export type PipTaskState = {
  activeConversationIds: string[];
  hiddenConversationIds: string[];
  alwaysHidden: boolean;
  /** For each conversation an agent drives something in: its placement and items, lead first. */
  conversations: Record<string, { placement: PipPlacement; items: PipItem[]; presented: boolean }>;
};

const NO_TASKS: PipTaskState = {
  activeConversationIds: [],
  hiddenConversationIds: [],
  alwaysHidden: false,
  conversations: {},
};

let taskState = NO_TASKS;
const listeners = new Set<() => void>();
let stopFollowing: (() => void) | null = null;

function setTaskState(next: PipTaskState | null | undefined): void {
  if (next === null || next === undefined) return;
  taskState = next;
  keepPipStills(
    new Set(
      Object.values(next.conversations).flatMap((conversation) =>
        conversation.items.map((item) => item.id),
      ),
    ),
  );
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (stopFollowing === null && isElectronDesktop()) {
    const stopState = window.crowdexDesktop?.listen('picture-in-picture-task-state', (payload) =>
      setTaskState(payload as PipTaskState),
    );
    const stopStills = followPipStills(true);
    stopFollowing = () => {
      stopState?.();
      stopStills();
    };
    void electronInvoke<PipTaskState>('pip_task_state')
      .then(setTaskState)
      .catch(() => undefined);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      stopFollowing?.();
      stopFollowing = null;
    }
  };
}

export function usePipTaskState(): PipTaskState {
  return useSyncExternalStore(
    subscribe,
    () => taskState,
    () => NO_TASKS,
  );
}

const command = (name: string, input: Record<string, unknown> = {}) =>
  void electronInvoke<PipTaskState | null>(name, input)
    .then(setTaskState)
    .catch(() => undefined);

export function setPipConversationVisible(conversationId: string, visible: boolean): void {
  command(visible ? 'pip_show_task' : 'pip_hide_task', { conversationId });
}

/** "Hide for all active chats": these and the next ones, until one is shown again. */
export function hideAllPip(): void {
  command('pip_hide_all');
}

export function setPipAlwaysHidden(alwaysHidden: boolean): void {
  command('pip_set_always_hidden', { alwaysHidden });
}

export function setPipPlacement(conversationId: string, placement: PipPlacement): void {
  command('pip_set_placement', { conversationId, placement });
}

/**
 * An item clicked: the lead brings what it shows to the front (the Mac app, or the tab), another
 * one comes to lead the stack (Codex's clicks).
 */
export function interactWithPipItem(id: string, lead: boolean): void {
  void electronInvoke('pip_interact', { id, focus: lead }).catch(() => undefined);
}

/**
 * The main window's side of the picture-in-picture: it tells the Desktop where the floating stack
 * may rest (the conversation pane's corners around the composer, and the summary panel's surface)
 * and opens the conversation, with its tab in front, whose item the user clicked.
 */
export function PictureInPictureHost({ enabled }: { enabled: boolean }) {
  const opener = useConversationOpener();
  const openerRef = useRef(opener);
  openerRef.current = opener;

  useEffect(() => {
    if (!enabled || !isElectronDesktop()) return;
    return startPipAnchorBridge(
      (layout) => void electronInvoke('pip_host_layout', { layout }).catch(() => undefined),
    );
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !isElectronDesktop()) return;
    return window.crowdexDesktop?.listen('picture-in-picture-open', (payload) => {
      const { conversationId, id, tabId } = payload as {
        conversationId: string;
        id: string;
        tabId: string;
      };
      void (openerRef.current?.openConversation(conversationId) ?? Promise.resolve(false))
        .then((opened) => (opened ? electronInvoke('pip_reveal_tab', { id, tabId }) : undefined))
        .catch(() => undefined);
    });
  }, [enabled]);

  return null;
}
